import messageIcon from '@raison/static/icons/message-2-line.svg';
import shieldIcon from '@raison/static/icons/shield-check-line.svg';
import {
    cn,
    maskEmail,
    maskPhone,
    RsnButton,
    RsnCopyButton,
    RsnDialog,
    RsnIconWidget,
    RsnInput,
    RsnMessage,
    RsnOtpInput,
    RsnSpinner,
    RsnTextButton,
    useNonReactiveCallback,
} from '@raison/ui-kit';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {QRCodeCanvas} from 'qrcode.react';
import React, {Fragment, useRef} from 'react';
import {useNavigate} from 'react-router';
import {DialogRegistration, GenericErrorMsg} from '~/entities/dialogs';
import {logoutNow, useVerificationMethodsQuery} from '~/entities/user';
import {createHttpLock, useLockEffect} from '~/library/network-facade';
import {useAppDispatch} from '~/shared/store';
import {SmsDeliveryStatus} from '../api/smsStatus';
import {TwoFaProvider} from '../model/TwoFaProvider';
import {VerificationMethodType} from '../model/VerificationMethod';
import {FallbackVerificationDialog} from './FallbackVerificationDialog';
import {OtpResendTimer, ResendTimerAtom} from './ResendTimer';
import {SmsErrorDialog} from './SmsErrorDialog';
import {VerificationMethodDialog} from './VerificationMethodsDialog';

export enum PinErrorCode {
    InvalidCode = 'CODE_INVALID',
    ExpiredCode = 'CODE_EXPIRED',
    SusActivity = 'ACCOUNT_LOCKED',
    Unknown = '__UNKNOWN__',
}

export function TwoFaMethodChoiceView({
    onMessagePick,
    onAppPick,
    isAppActivated,
    onActivationPick,
    onDialogClose,
    isActivationPromptOpen,
    isMethodPickerOpen,
    onMethodPickerOpen,
    onSettingsOpen,
}: {
    onMessagePick: (method: VerificationMethodType) => void;
    onAppPick: () => void;
    isAppActivated: boolean;
    onActivationPick: () => void;
    onDialogClose: () => void;
    isActivationPromptOpen: boolean;
    isMethodPickerOpen: boolean;
    onMethodPickerOpen: () => void;
    // FIXME: HACK: see forgetEntrySteps in crypto-send-and-receive
    onSettingsOpen?: () => void;
}) {
    const t = {
        ConfirmationMethod: 'Confirmation method',
        PleaseSelectMethod: 'Please select the method that you would like to confirm the operation.',
        AuthenticatorApp: 'Authenticator app',
        ActivateToUse: 'Activate to use this method',
        Message: 'Message',
        SmsMethodDescription: 'A one-time code will be sent to your phone number by SMS, Telegram, or WhatsApp',
        AppMethodDescription: 'A one-time code is generated in your authenticator app',
    };

    const methodsQueryOpt = useVerificationMethodsQuery();
    const methodsQuery = useQuery(methodsQueryOpt);
    const qc = useQueryClient();

    const ErrorDialog = useGenericErrorDialog();

    const beforeMessagePick = useDeduplicatedAsyncCallback(async () => {
        const methods = await qc.ensureQueryData(methodsQueryOpt).catch(error => {
            ErrorDialog.open();
            throw error;
        });
        if (methods.length === 0) {
            onMessagePick('sms');
            return;
        }
        if (methods.length === 1) {
            onMessagePick(methods[0]);
            return;
        }
        onMethodPickerOpen();
    });

    return (
        <div className='flex-1'>
            {renderFormHeading({
                title: t.ConfirmationMethod,
                details: t.PleaseSelectMethod,
            })}
            <div className='divide-y divide-solid divide-neutral-30'>
                {renderChoiceButton({
                    onClick: beforeMessagePick,
                    icon: messageIcon,
                    heading: t.Message,
                    details: t.SmsMethodDescription,
                })}
                {isAppActivated ? renderActivatedOtpButton() : renderNotActivatedOtpButton()}
            </div>

            <RsnDialog.Controller isOpen={isActivationPromptOpen} onClose={onDialogClose}>
                <TwoFaAppSettingsDialog onSettingsOpen={onSettingsOpen} />
            </RsnDialog.Controller>
            <RsnDialog.Controller onClose={onDialogClose}>
                {methodsQuery.isSuccess && isMethodPickerOpen && (
                    <VerificationMethodDialog methods={methodsQuery.data} onChange={onMessagePick} />
                )}
            </RsnDialog.Controller>
        </div>
    );

    function renderNotActivatedOtpButton() {
        return renderChoiceButton({
            onClick: onActivationPick,
            icon: shieldIcon,
            heading: t.AuthenticatorApp,
            details: <span className='text-warning-30'>{t.ActivateToUse}</span>,
        });
    }

    function renderActivatedOtpButton() {
        return renderChoiceButton({
            onClick: onAppPick,
            icon: shieldIcon,
            heading: t.AuthenticatorApp,
            details: t.AppMethodDescription,
        });
    }
}

function renderChoiceButton({
    onClick,
    icon,
    heading,
    afterHeading,
    details,
}: {
    onClick: () => void;
    icon: string;
    heading: React.ReactNode;
    afterHeading?: React.ReactNode;
    details: React.ReactNode;
}) {
    return (
        <div className='py-4'>
            <RsnIconWidget.Lg
                as='button'
                role='button'
                icon={RsnIconWidget.framedIcon(icon)}
                addon={<RsnIconWidget.Arrow />}
                onClick={onClick}
            >
                <RsnIconWidget.TextBlock multiline heading={heading} afterHeading={afterHeading} details={details} />
            </RsnIconWidget.Lg>
        </div>
    );
}

function renderFormHeading({title, details}: {title: string; details?: string | React.ReactElement}) {
    return (
        <hgroup className={cn('flex flex-col gap-2 pt-2')}>
            <h2 className='fv-heading-l text-neutral-100'>{title}</h2>
            {details && <p className='fv-text-l-regular text-neutral-90'>{details}</p>}
        </hgroup>
    );
}

interface TwoFaCodeFormProps {
    timer$: ResendTimerAtom | undefined;
    onRetryIntent: () => void;
    otp: string;
    onOtpChange: (value: string) => void;
    otpLength?: number;
    onInputComplete: (otp: string) => void;
    isPending: boolean;
    issue: string | undefined | null | false;
    heading: React.ReactElement;
    footnote?: React.ReactElement;
    onMethodChangeIntent?: () => void;
    deliveryInfo: null | SmsDeliveryStatus;
    onDeliveryInfoDismiss?: () => void;
}

const TwoFactorOtpInputId = 'RsnOtpInput-TwoFaCodeForm';

export function TwoFaCodeForm({
    timer$,
    onRetryIntent,
    otp,
    onOtpChange,
    otpLength = 6,
    onInputComplete,
    isPending,
    issue,
    heading,
    footnote,
    onMethodChangeIntent,
    deliveryInfo,
    onDeliveryInfoDismiss,
}: TwoFaCodeFormProps) {
    const t = {
        ChangeMethod: 'Change verification method',
    };

    const shouldDisable = isPending;

    const handleOtpInputChange = (value: string) => {
        // cleanup value from non-numeric value
        // inputMode='numeric' is not working for some reason
        const cleaned: string = value.replace(/\D/g, '');
        onOtpChange(cleaned);
    };

    return (
        <div className='flex flex-1 flex-col gap-6'>
            {heading}

            <RsnOtpInput
                id={TwoFactorOtpInputId}
                onComplete={() => {
                    onInputComplete(otp);
                }}
                value={otp}
                onChange={handleOtpInputChange}
                maxLength={otpLength}
                disabled={shouldDisable}
                issue={issue}
            />

            {timer$ && <OtpResendTimer disabled={shouldDisable} onRetryIntent={onRetryIntent} timer$={timer$} />}

            {onMethodChangeIntent && (
                <RsnTextButton.Inline disabled={shouldDisable} onClick={onMethodChangeIntent}>
                    {t.ChangeMethod}
                </RsnTextButton.Inline>
            )}

            {footnote}

            <div className='mt-auto grid place-items-center p-6'>
                {isPending && <RsnSpinner h={24} w={24} color='neutral60' />}
            </div>

            <RsnDialog.Controller onClose={onDeliveryInfoDismiss}>
                {(() => {
                    switch (deliveryInfo) {
                        case null:
                            return null;
                        case 'DELIVERED':
                            return <FallbackVerificationDialog />;
                        default:
                            return <SmsErrorDialog type={deliveryInfo} />;
                    }
                })()}
            </RsnDialog.Controller>
        </div>
    );
}

TwoFaCodeForm.focusOTPInput = function focusOtpInput() {
    const input = document.getElementById(TwoFactorOtpInputId);
    if (input instanceof HTMLInputElement) {
        input.focus();
    }
};

TwoFaCodeForm.ForCryptoTx = function TwoFaCodeFormForCryptoTx({
    provider,
    ...props
}: Omit<TwoFaCodeFormProps, 'heading' | 'footnote'> & {
    provider: TwoFaProvider;
    userPhone: string;
}) {
    let timerAtom: ResendTimerAtom | undefined;
    let heading: React.ReactElement;
    if (provider.kind === 'sms') {
        heading = <TwoFaCodeForm.SmsConfirmationHeading userPhone={props.userPhone} />;
        timerAtom = props.timer$;
    } else {
        heading = <TwoFaCodeForm.AppConfirmationHeading />;
        timerAtom = undefined;
    }
    return (
        <TwoFaCodeForm
            {...props}
            heading={heading}
            footnote={<TwoFaCodeForm.CryptoTransactionsFootnote />}
            timer$={timerAtom}
        />
    );
};

TwoFaCodeForm.Heading = renderFormHeading;

TwoFaCodeForm.SmsConfirmationHeading = function TwoFaCodeFormSmsConfirmationHeading({userPhone}: {userPhone: string}) {
    return renderFormHeading({
        title: 'Message confirmation',
        details: (
            <Fragment>
                {'Enter the one-time code we just sent '}
                <span className='whitespace-nowrap'>
                    {'to '}
                    <strong className='fv-text-l-bold'>{maskPhone(userPhone)}</strong>
                    {'.'}
                </span>
            </Fragment>
        ),
    });
};

TwoFaCodeForm.AppConfirmationHeading = function TwoFaCodeFormAppConfirmationHeading() {
    return renderFormHeading({
        title: 'Authenticator app confirmation',
        details: 'Enter the one-time code from your authenticator app.',
    });
};

TwoFaCodeForm.AppActivationHeader = function TwoFaCodeFormAppActivationHeader({userEmail}: {userEmail: string}) {
    return renderFormHeading({
        title: 'Activating authenticator app confirmation',
        details: (
            <Fragment>
                {'Enter the one-time code we just sent to '}
                <strong className='fv-text-l-bold whitespace-nowrap'>{maskEmail(userEmail)}</strong>
                {' to activate authenticator app confirmation.'}
            </Fragment>
        ),
    });
};

TwoFaCodeForm.AppDeactivationHeader = function TwoFaCodeFormAppDeactivationHeader({userEmail}: {userEmail: string}) {
    return renderFormHeading({
        title: 'Deactivating authenticator app confirmation',
        details: (
            <Fragment>
                {'Enter the one-time code we just sent to '}
                <strong className='fv-text-l-bold whitespace-nowrap'>{maskEmail(userEmail)}</strong>
                {' to deactivate authenticator app confirmation.'}
            </Fragment>
        ),
    });
};

TwoFaCodeForm.CryptoTransactionsFootnote = function TwoFaCodeFormCryptoTransactionsFootnote() {
    return (
        <RsnMessage.FootnoteV2>
            {'Once submitted, crypto transactions may not be reversible after they are confirmed on the blockchain.'}
        </RsnMessage.FootnoteV2>
    );
};

export function TwoFaAppSetupView({
    setupUrl,
    setupKey,
    generatedCode,
    onGeneratedCodeChange,
    onConfirm,
    isPending,
}: {
    setupUrl: string;
    setupKey: string;
    generatedCode: string;
    onGeneratedCodeChange: (value: string) => void;
    onConfirm: () => void;
    isPending: boolean;
}) {
    const t = {
        SettingUp: 'Setting up authenticator app confirmation',
        FirstStep: 'Open the authenticator app and scan the QR code or enter the key listed below.',
        SecondStep: 'Enter the generated confirmation code from authenticator app.',
        GeneratedCodeLabel: 'Generated code',
        Key: 'Key',
        Confirm: 'Confirm',
    };

    return (
        <form
            className='flex h-full flex-col gap-4'
            onSubmit={ev => {
                ev.preventDefault();
                onConfirm();
            }}
        >
            {renderFormHeading({
                title: t.SettingUp,
            })}
            {renderStepPoint(1, t.FirstStep)}
            <div className='self-center rounded-lg border border-solid border-neutral-40 p-6'>
                <QRCodeCanvas value={setupUrl} size={214} />
            </div>
            <label className='flex gap-4 rounded-lg bg-neutral-20 p-4'>
                <div className='grid flex-1 gap-1'>
                    <div className='fv-text-m-regular text-neutral-60'>{t.Key}</div>
                    <div className='fv-text-xl-regular break-all text-neutral-100'>{renderKey(setupKey)}</div>
                </div>

                <div className='grid place-items-center'>
                    <RsnCopyButton value={setupKey} />
                </div>
            </label>
            {renderStepPoint(2, t.SecondStep)}
            <RsnInput
                label={t.GeneratedCodeLabel}
                inputMode='numeric'
                maxLength={6}
                type='text'
                value={generatedCode}
                onChange={ev => onGeneratedCodeChange(ev.target.value)}
            />

            <div className='mt-auto'>
                <RsnButton.Primary disabled={isPending} type='submit'>
                    {t.Confirm}
                </RsnButton.Primary>
            </div>
        </form>
    );

    function renderStepPoint(number: number, text: string) {
        return (
            <div className='mt-2 flex gap-4'>
                <div className='fv-text-l-bold grid size-9 shrink-0 place-items-center rounded-full bg-neutral-20'>
                    {number}
                </div>
                <div className='fv-text-l-regular text-neutral-90'>{text}</div>
            </div>
        );
    }

    function renderKey(setupKey: string) {
        // no formatting
        return setupKey;

        // or formatting with dashes
        // let result = setupKey.substring(0, 4);
        // for (let i = 4; i < setupKey.length; i += 4) {
        //     result += `-${setupKey.substring(i, i + 4)}`;
        // }
        // return result;
    }
}

export function TwoFaAppSettingsDialog({onSettingsOpen}: {onSettingsOpen?: () => void}) {
    const navigate = useNavigate();
    const goToSettings = () => {
        onSettingsOpen?.();
        navigate('/settings', {replace: true});
    };
    return (
        <RsnDialog.Window>
            <RsnDialog.CenteredHeader icon={{element: <RsnDialog.StatusIcon status='attention' />}}>
                {'Authenticator app is not activated'}
            </RsnDialog.CenteredHeader>
            <RsnDialog.Body>{'To use this method, activate it in Settings.'}</RsnDialog.Body>
            <RsnDialog.Footer column>
                <RsnButton.Primary onClick={goToSettings}>{'Open Settings'}</RsnButton.Primary>
                <RsnButton.Alike intent='secondary' as={RsnDialog.CloseTrigger}>
                    {'Later'}
                </RsnButton.Alike>
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );
}

interface InvalidCodeDialogProps {
    onClose?: () => void;
}
export function InvalidCodeDialog({onClose}: InvalidCodeDialogProps) {
    return (
        <RsnDialog.Window onExitEnd={onClose}>
            <RsnDialog.CenteredHeader
                text={InvalidCodeDialog.texts.title}
                icon={{element: <RsnDialog.StatusIcon status='alert' />}}
            />
            <RsnDialog.Body>{InvalidCodeDialog.texts.details}</RsnDialog.Body>
            <RsnDialog.Footer>
                <RsnButton.Alike intent='secondary' as={RsnDialog.CloseTrigger}>
                    {'I got it'}
                </RsnButton.Alike>
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );
}

InvalidCodeDialog.open = ({onClose}: InvalidCodeDialogProps): AppThunkAction<void> => {
    return dispatch => {
        dispatch(GenericErrorMsg.open({...InvalidCodeDialog.texts, onExitEnd: onClose}));
    };
};
InvalidCodeDialog.texts = {
    title: 'Invalid code',
    details: 'You entered the wrong code. Check the correctness and try again.',
};

export const SusActivityDialog = (() => {
    function SusActivityDialog() {
        const navigate = useNavigate();

        useLockEffect(SusActivityDialog.httpLock);

        const goToSupport = () => {
            navigate('/support/form');
        };
        const dispatch = useAppDispatch();
        return (
            <RsnDialog.Window
                onExitEnd={() => {
                    dispatch(logoutNow());
                }}
            >
                <RsnDialog.CenteredHeader
                    text={'Suspicious activity detected'}
                    icon={{element: <RsnDialog.StatusIcon status='alert' />}}
                />
                <RsnDialog.Body>
                    {
                        'For your security, we’ve temporarily blocked your account. After closing this message, you will be logged out. Please contact support to restore access.'
                    }
                </RsnDialog.Body>
                <RsnDialog.Footer>
                    <RsnTextButton.Block onClick={goToSupport}>{'Contact support'}</RsnTextButton.Block>
                </RsnDialog.Footer>
            </RsnDialog.Window>
        );
    }

    SusActivityDialog.httpLock = createHttpLock();

    const registration = DialogRegistration.create({
        name: 'SuspiciousActivity',
        Component: SusActivityDialog,
    });

    return Object.assign(SusActivityDialog, registration);
})();

function useDeduplicatedAsyncCallback<F extends (...args: any[]) => Promise<any>>(callback: F) {
    const isRunning = useRef(false);
    return useNonReactiveCallback(async (...args: Parameters<F>) => {
        try {
            isRunning.current = true;
            return await callback(...args);
        } finally {
            isRunning.current = false;
        }
    });
}

function useGenericErrorDialog() {
    const dispatch = useAppDispatch();

    const open = () => {
        dispatch(GenericErrorMsg.open());
    };
    return {open};
}
